/**
 * Handlers for individual product field changes
 */

// Helper functions for content manipulation

// Append content with proper newlines for regular text fields
const appendContent = (existing, newContent) => {
  // Handle null or undefined values safely
  const existingStr = existing !== null && existing !== undefined ? 
    (typeof existing === 'string' ? existing : String(existing)) : '';
  
  const newContentStr = newContent !== null && newContent !== undefined ? 
    (typeof newContent === 'string' ? newContent : String(newContent)) : '';
  
  // Log the values to help with debugging
  console.log('appendContent called with:', {
    existing: existingStr ? existingStr.substring(0, 50) + '...' : '(empty)',
    newContent: newContentStr ? newContentStr.substring(0, 50) + '...' : '(empty)'
  });
  
  // If both values are empty, return empty string
  if (!existingStr && !newContentStr) {
    return '';
  }
  
  // If one value is empty, return the other
  if (!existingStr) return newContentStr;
  if (!newContentStr) return existingStr;
  
  // Join with double newlines
  return existingStr + String.fromCharCode(10) + String.fromCharCode(10) + newContentStr;
};

// Special handler for HTML content (like body_html)
const appendHtmlContent = (existing, newContent) => {
  console.log('appendHtmlContent called with:', { existing: existing.substring(0, 50) + '...', newContent: newContent.substring(0, 50) + '...' });
  
  // Check if the content has HTML tags
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(existing) || /<[a-z][\s\S]*>/i.test(newContent);
  console.log('Content has HTML tags:', hasHtmlTags);
  
  // Always ensure both parts have proper HTML formatting
  // If existing content doesn't have paragraph tags and isn't empty, wrap it
  if (existing && existing.trim() !== '' && !existing.includes('<p>')) {
    existing = `<p>${existing}</p>`;
    console.log('Wrapped existing content in paragraph tags');
  }
  
  // If new content doesn't have paragraph tags and isn't empty, wrap it
  if (newContent && newContent.trim() !== '' && !newContent.includes('<p>')) {
    newContent = `<p>${newContent}</p>`;
    console.log('Wrapped new content in paragraph tags');
  }
  
  // Combine the content with double line breaks to ensure separation
  const combined = existing + String.fromCharCode(10) + String.fromCharCode(10) + newContent;
  console.log('Combined content (first 100 chars):', combined.substring(0, 100) + '...');
  return combined;
};

export const createProductFieldHandlers = (setLocalEdits, setHasUnsavedChanges, showToast) => {
  /**
   * Handle changes to product option names and values
   */
  const handleOptionChange = (productId, optionIndex, field, value, oldValue = null) => {
    console.log(`handleOptionChange: Product ${productId}, Option ${optionIndex}, Field ${field}, Value:`, value);
    
    try {
      // Create a copy of localEdits to work with
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };
        
        // Initialize product in localEdits if needed
        if (!updatedEdits[productId]) {
          updatedEdits[productId] = { _timestamp: Date.now() };
        }
        
        // Initialize options array if needed
        if (!updatedEdits[productId].options) {
          updatedEdits[productId].options = [];
        }
        
        // Ensure the option exists at the specified index
        while (updatedEdits[productId].options.length <= optionIndex) {
          updatedEdits[productId].options.push({});
        }
        
        // Update the field
        updatedEdits[productId].options[optionIndex][field] = value;
        
        // Update timestamp to force re-render
        updatedEdits[productId]._timestamp = Date.now();
        
        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Error in handleOptionChange:', error);
      showToast('Failed to update option', true);
    }
  };

  /**
   * Handle changes to option names (Option 1, Option 2, Option 3)
   */
  const handleOptionNameChange = (productId, optionIndex, value) => {
    console.log(`handleOptionNameChange: Product ${productId}, Option ${optionIndex}, Value:`, value);
    
    try {
      // Map option index to the correct field name
      const optionFieldName = `option${optionIndex + 1}_name`;
      
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };
        
        // Initialize product in localEdits if needed
        if (!updatedEdits[productId]) {
          updatedEdits[productId] = { _timestamp: Date.now() };
        }
        
        // Update the option name directly
        updatedEdits[productId][optionFieldName] = value;
        
        // Also update in options array for consistency
        handleOptionChange(productId, optionIndex, 'name', value);
        
        // Update timestamp to force re-render
        updatedEdits[productId]._timestamp = Date.now();
        
        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Error in handleOptionNameChange:', error);
      showToast('Failed to update option name', true);
    }
  };

  /**
   * Handle changes to individual option values
   */
  // This function is no longer needed - option values are handled directly in UI

  /**
   * Handle changes to multiple option values at once
   */
  const handleOptionValuesChange = (productId, optionIndex, values) => {
    console.log(`handleOptionValuesChange: Product ${productId}, Option ${optionIndex}, Values:`, values);
    handleOptionChange(productId, optionIndex, 'values', values);
  };

  /**
   * Handle changes to product fields (title, description, etc.)
   */
  const handleProductChange = (productId, field, value, insertMode = 'exchange') => {
    console.log(`handleProductChange: Product ${productId}, Field ${field}, Insert Mode: ${insertMode}`);
    
    try {
      // Special handling for 'all' field - process all fields from the value object
      if (field === 'all' && typeof value === 'object' && value !== null) {
        console.log(`Processing multiple fields for product ${productId}:`, value.insertModes || 'No insert modes specified');
        
        // Check if value has insertModes
        const insertModes = value.insertModes || {};
        
        // Check if we have original content as backup
        const originalContent = value._originalContent || {};
        if (Object.keys(originalContent).length > 0) {
          console.log('Found original content backup:', Object.keys(originalContent));
        }
        
        // First process metafields if present
        if (value.metafields && typeof value.metafields === 'object') {
          console.log('Processing metafields from updates:', Object.keys(value.metafields));
          
          Object.entries(value.metafields).forEach(([fieldName, fieldValue]) => {
            // Get the insert mode for this metafield
            const fieldInsertMode = insertModes[fieldName] || 'exchange';
            console.log(`Processing metafield ${fieldName} with mode ${fieldInsertMode}`);
            
            // Handle the metafield with the correct insert mode
            const metafieldId = fieldName.replace('metafield.', '');
            
            // Log the metafield value for debugging
            console.log(`Metafield value for ${fieldName}:`, {
              type: typeof fieldValue,
              length: typeof fieldValue === 'string' ? fieldValue.length : 'N/A',
              preview: typeof fieldValue === 'string' ? fieldValue.substring(0, 50) + '...' : fieldValue
            });
            
            // Pass the value directly with the specified insert mode
            handleMetafieldChange(productId, metafieldId, fieldValue, null, fieldInsertMode);
          });
        }
        
        // Iterate through all fields in the value object except for special properties
        Object.entries(value).forEach(([fieldName, fieldValue]) => {
          // Skip special properties and metafields (handled separately)
          if (fieldName === 'insertModes' || fieldName === '_originalContent' || 
              fieldName === 'metafields' || fieldName.startsWith('metafield.')) {
            return;
          }
          
          // Skip null or undefined values
          if (fieldValue === null || fieldValue === undefined) {
            return;
          }
          
          // Determine the insert mode for this particular field
          // Use the specified insert mode, or default to 'exchange'
          let fieldInsertMode = insertModes[fieldName] || 'exchange';
          
          console.log(`Processing field ${fieldName} with mode ${fieldInsertMode}`);

          // Regular product field handling
          if (fieldInsertMode === 'add' && originalContent[fieldName]) {
            // Create a special value object with the original content
            const valueWithOriginal = {
              _originalContent: { [fieldName]: originalContent[fieldName] },
              [fieldName]: fieldValue
            };
            handleProductChange(productId, fieldName, valueWithOriginal, 'add');
          } else {
            // Just pass the value directly with the correct insert mode
            handleProductChange(productId, fieldName, fieldValue, fieldInsertMode);
          }
        });
        
        return; // Exit after processing all fields
      }
      
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };
        
        // Initialize product in localEdits if needed
        if (!updatedEdits[productId]) {
          updatedEdits[productId] = { _timestamp: Date.now() };
        }
        
        // Get the original product data to check for existing content
        let originalContent = '';
        
        // CRITICAL: First check if we have _originalContent passed directly
        if (value && typeof value === 'object' && value._originalContent && value._originalContent[field]) {
          originalContent = value._originalContent[field];
          console.log(`Using directly passed original content for ${field}: ${originalContent.substring(0, 50)}...`);
        }
        // CRITICAL: For body_html field, we need to check multiple sources if not passed directly
        else if (field === 'body_html') {
          console.log(`Special handling for body_html field to find existing content`);
          
          // First check if we already have the content in local edits
          if (updatedEdits[productId] && updatedEdits[productId][field]) {
            originalContent = updatedEdits[productId][field];
            console.log(`Found existing content in local edits: ${originalContent.substring(0, 50)}...`);
          }
          // Then check global shopifyProducts array
          else if (typeof window !== 'undefined' && window.shopifyProducts) {
            // Extract numeric ID from GID format if needed
            let searchId = productId;
            if (productId.includes('gid://')) {
              const parts = productId.split('/');
              searchId = parts[parts.length - 1];
              console.log(`Extracted numeric ID ${searchId} from GID ${productId}`);
            }
            
            // Try to find product by ID in different formats - use a simpler approach
            try {
              const products = window.shopifyProducts || [];
              console.log(`Searching through ${products.length} products for ID ${searchId}`);
              
              // Find the product by ID using a more direct approach
              const matchingProduct = products.find(p => {
                if (!p || !p.id) return false;
                
                // Try exact match first
                if (p.id === productId) return true;
                
                // Try numeric ID match
                if (p.id === searchId || String(p.id) === searchId) return true;
                
                // Try GID format match
                if (typeof p.id === 'string' && p.id.includes('/')) {
                  const numericId = p.id.split('/').pop();
                  return numericId === searchId;
                }
                
                return false;
              });
              
              if (matchingProduct) {
                console.log(`Found product match: ${matchingProduct.title || 'Untitled'}`);
                
                // Check for body_html directly first
                if (matchingProduct.body_html) {
                  originalContent = matchingProduct.body_html;
                  console.log(`Found body_html content: ${originalContent.substring(0, 50)}...`);
                }
                // Then try descriptionHtml
                else if (matchingProduct.descriptionHtml) {
                  originalContent = matchingProduct.descriptionHtml;
                  console.log(`Found descriptionHtml content: ${originalContent.substring(0, 50)}...`);
                }
                // Then try description
                else if (matchingProduct.description) {
                  originalContent = matchingProduct.description;
                  console.log(`Found description content: ${originalContent.substring(0, 50)}...`);
                }
                // Then try metafields if available
                else if (matchingProduct.metafields && Array.isArray(matchingProduct.metafields)) {
                  const descHtmlMetafield = matchingProduct.metafields.find(m => m.key === 'description_html');
                  if (descHtmlMetafield && descHtmlMetafield.value) {
                    originalContent = descHtmlMetafield.value;
                    console.log(`Found description_html metafield content: ${originalContent.substring(0, 50)}...`);
                  } else {
                    const seoDescMetafield = matchingProduct.metafields.find(m => m.key === 'description' && m.namespace === 'seo');
                    if (seoDescMetafield && seoDescMetafield.value) {
                      originalContent = seoDescMetafield.value;
                      console.log(`Found SEO description metafield content: ${originalContent.substring(0, 50)}...`);
                    }
                  }
                }
              }
            } catch (err) {
              console.error('Error searching for product:', err);
            }
            
            if (!originalContent) {
              console.log(`Could not find any content for body_html field in any product`);
            }
          }
        }
        // Standard handling for other fields
        else if (typeof window !== 'undefined' && window.shopifyProducts) {
          // Try to find product by ID
          let searchId = productId;
          if (productId.includes('gid://')) {
            const parts = productId.split('/');
            searchId = parts[parts.length - 1];
          }
          
          const originalProduct = window.shopifyProducts.find(p => 
            p.id === productId || p.id === searchId || String(p.id) === searchId
          );
          
          if (originalProduct && originalProduct[field]) {
            originalContent = originalProduct[field];
            console.log(`Found original content for ${field}: ${originalContent.substring(0, 50)}...`);
          }
        }
        
        // Handle different insert modes
        if (insertMode === 'add') {
          // For 'add' mode, append to existing content if available
          const existingContent = updatedEdits[productId][field] || originalContent;
          if (existingContent) {
            console.log(`Adding to existing content for ${field} using 'add' mode`);
            
            // Use special handler for HTML content (body_html)
            if (field === 'body_html') {
              console.log(`Using HTML content handler for body_html field`);
              // Ensure we're passing the right value - if it's an object, extract the field value
              const contentToAdd = typeof value === 'object' && value[field] ? value[field] : value;
              console.log(`Content to add: ${contentToAdd.substring(0, 50)}...`);
              const combinedContent = appendHtmlContent(existingContent, contentToAdd);
              updatedEdits[productId][field] = combinedContent;
              
              // CRITICAL: Also update descriptionHtml to ensure UI displays correctly
              console.log(`Setting descriptionHtml to match combined body_html content`);
              updatedEdits[productId]['descriptionHtml'] = combinedContent;
            } else {
              // Use regular content append for other fields
              // Ensure we're passing the right value - if it's an object, extract the field value
              const contentToAdd = typeof value === 'object' && value[field] ? value[field] : value;
              console.log(`Regular content to add to ${field}: ${typeof contentToAdd === 'string' ? contentToAdd.substring(0, 50) : contentToAdd}...`);
              updatedEdits[productId][field] = appendContent(existingContent, contentToAdd);
            }
          } else {
            console.log(`No existing content found for ${field}, setting directly`);
            // For body_html, ensure proper HTML formatting even when setting directly
            if (field === 'body_html') {
              let formattedValue = value;
              if (!formattedValue.includes('<p>')) {
                console.log(`Formatting body_html content with paragraph tags`);
                formattedValue = `<p>${value}</p>`;
              }
              updatedEdits[productId][field] = formattedValue;
              
              // CRITICAL: Also update descriptionHtml to ensure UI displays correctly
              console.log(`Setting descriptionHtml to match body_html content with add mode`);
              // We need to use the same 'add' mode for descriptionHtml
              // But we can't call handleProductChange recursively, so we'll set it directly
              // and handle the insert mode ourselves
              updatedEdits[productId]['descriptionHtml'] = formattedValue;
              
              // Mark this field as already processed with 'add' mode
              if (!updatedEdits[productId]._processedFields) {
                updatedEdits[productId]._processedFields = {};
              }
              updatedEdits[productId]._processedFields['descriptionHtml'] = 'add';
            } else {
              updatedEdits[productId][field] = value;
            }
          }
        } else {
          // For 'exchange' mode, just set the value
          console.log(`Using 'exchange' mode for ${field}`);
          
          // CRITICAL: For workflow scenarios, only prevent truly empty/null values from overwriting
          // But allow templates to intentionally replace content when they provide meaningful values
          if (field === 'body_html' || field === 'descriptionHtml') {
            // Only skip if the value is truly empty/null/undefined (not just a different value)
            if (value === null || value === undefined || value === '') {
              const existingValue = updatedEdits[productId][field];
              const hasExistingValue = existingValue && existingValue.trim() !== '';
              
              if (hasExistingValue) {
                console.log(`Skipping null/empty ${field} value to preserve existing content`);
                return updatedEdits; // Don't update if new value is truly empty but existing value exists
              }
            }
            // If we reach here, the value is meaningful and should be set (even if it replaces existing content)
          }
          
          updatedEdits[productId][field] = value;
          
          // NOTE: Do NOT automatically update descriptionHtml when body_html changes
          // TemplateTools.processTemplateResults handles the body_html -> descriptionHtml mapping
          // with the correct insert mode from the template. Doing it here would cause double processing.
        }
        
        // Update timestamp to force re-render
        updatedEdits[productId]._timestamp = Date.now();
        
        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Error in handleProductChange:', error);
      showToast('Failed to update product', true);
    }
  };

  /**
   * Handle changes to product metafields
   */
  const handleMetafieldChange = (productId, metafieldId, value, e, insertMode = 'exchange') => {
    console.log(`handleMetafieldChange: Product ${productId}, Metafield ${metafieldId}, Value:`, value, `Insert Mode: ${insertMode}`);
    
    // Prevent event propagation if event is provided
    if (e && e.stopPropagation) {
      console.log('Stopping propagation in handleMetafieldChange');
      e.stopPropagation();
    }
    
    try {
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };
        
        // Initialize product in localEdits if needed
        if (!updatedEdits[productId]) {
          updatedEdits[productId] = { _timestamp: Date.now() };
          console.log(`Created new product entry in localEdits for product ${productId}`);
        }
        
        // Initialize metafields object if needed
        if (!updatedEdits[productId].metafields) {
          updatedEdits[productId].metafields = {};
          console.log(`Created new metafields object for product ${productId}`);
        }
        
        // Parse namespace and key from metafieldId
        let namespace, key;
        if (metafieldId.includes('.')) {
          [namespace, key] = metafieldId.split('.');
        } else {
          console.error(`Invalid metafield ID format: ${metafieldId}`);
          return updatedEdits;
        }
        
        // Handle different insertion modes
        let finalValue = value;
        
        // Process 'add' mode if requested and we have existing content
        if (insertMode === 'add') {
          console.log(`Using 'add' mode for metafield ${metafieldId}`);
          
          // Try to find existing metafield value from multiple sources
          let existingValue = null;
          
          // 1. Check previous edits for the metafield value - this is critical for local edits
          if (prevEdits[productId]?.metafields?.[metafieldId]) {
            existingValue = prevEdits[productId].metafields[metafieldId].value;
            console.log(`Found existing metafield in previous edits: ${metafieldId}`, {
              value: typeof existingValue === 'string' ? existingValue.substring(0, 50) + '...' : existingValue,
              type: typeof existingValue
            });
          }
          
          // 2. Check if the metafield exists in the product's metafields array
          if (!existingValue && typeof window !== 'undefined' && window.shopifyProducts) {
            // Try to find product by ID
            let searchId = productId;
            if (productId.includes('gid://')) {
              const parts = productId.split('/');
              searchId = parts[parts.length - 1];
            }
            
            const originalProduct = window.shopifyProducts.find(p => 
              p.id === productId || p.id === searchId || String(p.id) === searchId
            );
            
            if (originalProduct && originalProduct.metafields) {
              const metafield = originalProduct.metafields.find(m => 
                m.namespace === namespace && m.key === key
              );
              
              if (metafield && metafield.value) {
                existingValue = metafield.value;
                console.log(`Found existing metafield in original product: ${existingValue.substring(0, 50)}...`);
              }
            }
          }
          
          if (existingValue) {
            console.log(`Found existing content for metafield ${metafieldId}: ${existingValue.substring(0, 50)}...`);
            
            // Extract the content to add if the value is an object
            let contentToAdd = value;
            
            // Handle different formats of the value
            if (typeof value === 'object') {
              // Check if the value has the metafieldId as a property (common in template results)
              if (value[metafieldId]) {
                contentToAdd = value[metafieldId];
              } 
              // Check if it's in the metafields object (common in bulk updates)
              else if (value.metafields && value.metafields[metafieldId]) {
                contentToAdd = value.metafields[metafieldId];
              }
            }
            
            console.log(`Content to add for ${metafieldId}:`, {
              value: typeof contentToAdd === 'string' ? contentToAdd.substring(0, 50) + '...' : contentToAdd,
              type: typeof contentToAdd
            });
            
            // Combine the content using appendContent helper
            finalValue = appendContent(existingValue, contentToAdd);
            console.log(`Combined content for ${metafieldId} (first 50 chars): ${finalValue.substring(0, 50)}...`);
          } else {
            console.log(`No existing content found for metafield ${metafieldId}, setting directly`);
          }
        }
        
        // Update the metafield
        updatedEdits[productId].metafields[metafieldId] = {
          value: finalValue,
          _timestamp: Date.now()
        };
        
        // Also add to metafield_updates for API compatibility
        if (!updatedEdits[productId].metafield_updates) {
          updatedEdits[productId].metafield_updates = {};
        }
        updatedEdits[productId].metafield_updates[metafieldId] = value;
        
        // Add to metafields_array for GraphQL API compatibility
        if (!updatedEdits[productId].metafields_array) {
          updatedEdits[productId].metafields_array = [];
        }
        
        // Check if the metafield already exists in the array
        const existingIndex = updatedEdits[productId].metafields_array.findIndex(
          m => m.namespace === namespace && m.key === key
        );
        
        if (existingIndex >= 0) {
          // Update existing metafield
          updatedEdits[productId].metafields_array[existingIndex].value = value;
          // Always use single_line_text_field for safety - Shopify API requires types to match definitions
          updatedEdits[productId].metafields_array[existingIndex].type = 'single_line_text_field';
        } else {
          // Add new metafield
          updatedEdits[productId].metafields_array.push({
            namespace,
            key,
            value,
            type: 'single_line_text_field' // Always use single_line_text_field for safety
          });
        }
        
        // Update timestamp to force re-render
        updatedEdits[productId]._timestamp = Date.now();
        
        console.log(`Updated metafield ${metafieldId} for product ${productId}:`, updatedEdits[productId].metafields[metafieldId]);
        console.log(`Current localEdits structure for product ${productId}:`, updatedEdits[productId]);
        
        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Error in handleMetafieldChange:', error);
      showToast('Failed to update metafield', true);
    }
  };

  /**
   * Handle changes to variant fields (price, sku, etc.)
   */
  const handleVariantChange = (productId, variantId, field, value) => {
    console.log(`handleVariantChange: Product ${productId}, Variant ${variantId}, Field ${field}, Value:`, value);
    
    try {
      // Standardize field names to ensure consistency
      let standardizedField = field;
      let standardizedValue = value;
      
      // Map field names to consistent format
      if (field === 'inventory_quantity') {
        standardizedField = 'inventoryQuantity';
      } else if (field === 'compare_at_price') {
        standardizedField = 'compareAtPrice';
      }
      
      // Use the productUtils.setVariantFieldValue function to ensure proper initialization
      // with complete variant data including images
      const { setVariantFieldValue } = require('../utils/productUtils');
      
      setLocalEdits(prevEdits => {
        // Use the utility function to update the variant field
        // This will ensure that new variant entries are initialized with complete data
        const updatedEdits = setVariantFieldValue(prevEdits, productId, variantId, standardizedField, standardizedValue);
        
        // Update timestamp to force re-render
        if (updatedEdits[productId]) {
          updatedEdits[productId]._timestamp = Date.now();
        }
        
        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Error in handleVariantChange:', error);
      showToast('Failed to update variant', true);
    }
  };

  /**
   * Handle changes to multiple variants at once (e.g., from template results)
   * This is used when we need to update multiple variants with SKUs
   */
  const handleMultipleVariantChanges = (productId, variantUpdates) => {
    console.log(`handleMultipleVariantChanges: Product ${productId}, Updates for ${Object.keys(variantUpdates).length} variants`);
    
    try {
      const { setVariantFieldValue } = require('../utils/productUtils');
      
      setLocalEdits(prevEdits => {
        let updatedEdits = { ...prevEdits };
        
        // First, retrieve existing SKU values for all variants to preserve them
        const existingVariantData = {};
        if (updatedEdits[productId] && updatedEdits[productId].variants) {
          Object.entries(updatedEdits[productId].variants).forEach(([variantId, variantData]) => {
            existingVariantData[variantId] = { ...variantData };
          });
        }
        
        // Process each variant update
        Object.entries(variantUpdates).forEach(([variantId, fields]) => {
          // Apply each field update to the variant
          Object.entries(fields).forEach(([field, value]) => {
            // Standardize field names
            let standardizedField = field;
            if (field === 'inventory_quantity') {
              standardizedField = 'inventoryQuantity';
            } else if (field === 'compare_at_price') {
              standardizedField = 'compareAtPrice';
            }
            
            // Make sure price values are properly formatted and not treated as 0
            let processedValue = value;
            if (field === 'price' && value) {
              console.log(`Processing price value: ${value} (type: ${typeof value})`);
              // Ensure price is treated as a string to preserve decimal places
              processedValue = value.toString();
            }
            
            // Apply the update
            updatedEdits = setVariantFieldValue(
              updatedEdits, 
              productId, 
              variantId, 
              standardizedField, 
              processedValue
            );
            
            console.log(`Applied ${field}=${processedValue} to variant ${variantId}`);
          });
        });
        
        // Ensure SKUs are preserved when only updating prices
        // This is critical for the update_prices tool template
        if (Object.values(variantUpdates).every(fields => 'price' in fields && Object.keys(fields).length === 1)) {
          console.log('Detected price-only update, preserving existing SKUs');
          
          Object.entries(existingVariantData).forEach(([variantId, variantData]) => {
            if (variantData.sku && updatedEdits[productId] && updatedEdits[productId].variants && 
                updatedEdits[productId].variants[variantId]) {
              // Preserve the existing SKU
              updatedEdits[productId].variants[variantId].sku = variantData.sku;
              console.log(`Preserved existing SKU ${variantData.sku} for variant ${variantId}`);
            }
          });
        }
        
        // Update timestamp to force re-render
        if (updatedEdits[productId]) {
          updatedEdits[productId]._timestamp = Date.now();
        }
        
        return updatedEdits;
      });
      
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Error in handleMultipleVariantChanges:', error);
      showToast('Failed to update variants', true);
    }
  };

  /**
   * Generic field change handler that routes to the appropriate specific handler
   */
  const handleFieldChange = (productId, field, value) => {
    console.log(`handleFieldChange: Product ${productId}, Field ${field}, Value:`, value);
    
    // Extract field type and specific field from the field string
    const [fieldType, specificField] = field.split('.');
    
    try {
      switch (fieldType) {
        case 'product':
          handleProductChange(productId, specificField, value);
          break;
        case 'variant':
          const [, variantId, variantField] = field.split('.');
          handleVariantChange(productId, variantId, variantField, value);
          break;
        case 'option':
          const [, optionIndex, optionField] = field.split('.');
          handleOptionChange(productId, parseInt(optionIndex), optionField, value);
          break;
        case 'metafield':
          const metafieldId = field.replace('metafield.', '');
          
          // Check if value is an object with insertMode property
          let insertMode = 'exchange';
          if (typeof value === 'object' && value.insertModes && value.insertModes[field]) {
            insertMode = value.insertModes[field];
            console.log(`Found insertMode '${insertMode}' for metafield ${metafieldId}`);
          }
          
          // Pass the insertMode parameter to handleMetafieldChange
          handleMetafieldChange(productId, metafieldId, value, null, insertMode);
          break;
        default:
          handleProductChange(productId, field, value);
          break;
      }
    } catch (error) {
      console.error('Error in handleFieldChange:', error);
      showToast('Failed to update field', true);
    }
  };

  // Function to register external product change handlers
  let externalProductChangeHandler = null;
  
  const registerHandleProductChange = (handler) => {
    console.log('Registering external product change handler');
    externalProductChangeHandler = handler;
  };

  /**
   * Event handler wrappers with propagation prevention
   */

  // Event handler for TextField onChange with propagation prevention
  const handleTextFieldChange = (handler) => {
    return (value, e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      
      // Log the value being passed to ensure it's correct
      console.log('TextField change value:', value);
      
      // Call the handler with the updated value
      handler(value);
    };
  };

  // Event handler for TextField onClearButtonClick with propagation prevention
  const handleClearButtonClick = (handler) => {
    return (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      handler();
    };
  };

  // Event handler for TextField onFocus with propagation prevention
  const handleFocus = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
  };

  // Event handler for TextField onClick with propagation prevention
  const handleClick = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
  };

  // Wrapper for product field change with event handling
  const createProductFieldChangeHandler = (productId, field) => {
    return {
      onChange: handleTextFieldChange((value) => handleProductChange(productId, field, value)),
      onClearButtonClick: handleClearButtonClick(() => handleProductChange(productId, field, '')),
      onFocus: handleFocus,
      onClick: handleClick
    };
  };

  // Wrapper for variant field change with event handling
  const createVariantFieldChangeHandler = (productId, variantId, field, formatForApi) => {
    return {
      onChange: handleTextFieldChange((value) => {
        const formattedValue = formatForApi ? formatForApi(value) : value;
        handleVariantChange(productId, variantId, field, formattedValue);
      }),
      onClearButtonClick: handleClearButtonClick(() => handleVariantChange(productId, variantId, field, '')),
      onFocus: handleFocus,
      onClick: handleClick
    };
  };

  // Wrapper for option name change with event handling
  const createOptionNameChangeHandler = (productId, optionIndex) => {
    return {
      onChange: handleTextFieldChange((value) => handleOptionNameChange(productId, optionIndex, value)),
      onClearButtonClick: handleClearButtonClick(() => handleOptionNameChange(productId, optionIndex, '')),
      onFocus: handleFocus,
      onClick: handleClick
    };
  };

  // Handle option value changes - simple direct update to localEdits
  const handleOptionValueChange = (productId, optionIndex, valueIndex, value) => {
    console.log(`Option value change: Product ${productId}, Option ${optionIndex + 1}, Value ${valueIndex} = "${value}"`);
    
    // Create the key for the option value
    const key = `option${optionIndex + 1}_value_${valueIndex}`;
    
    // Update the product directly using handleProductChange
    handleProductChange(productId, key, value);
  };

  // Wrapper for option value change with event handling
  const createOptionValueChangeHandler = (productId, optionIndex, valueIndex) => {
    return {
      onChange: handleTextFieldChange((value) => handleOptionValueChange(productId, optionIndex, valueIndex, value)),
      onClearButtonClick: handleClearButtonClick(() => handleOptionValueChange(productId, optionIndex, valueIndex, '')),
      onFocus: handleFocus,
      onClick: handleClick
    };
  };

  // Wrapper for metafield change with event handling
  const createMetafieldChangeHandler = (productId, namespace, key) => {
    const metafieldId = `${namespace}.${key}`;
    return {
      onChange: handleTextFieldChange((value) => handleMetafieldChange(productId, metafieldId, value)),
      onClearButtonClick: handleClearButtonClick(() => handleMetafieldChange(productId, metafieldId, '')),
      onFocus: handleFocus,
      onClick: handleClick
    };
  };

  return {
    handleOptionChange,
    handleOptionNameChange,
    handleOptionValueChange,
    handleOptionValuesChange,
    handleProductChange,
    handleMetafieldChange,
    handleVariantChange,
    handleMultipleVariantChanges,
    createProductFieldChangeHandler,
    createVariantFieldChangeHandler,
    createOptionNameChangeHandler,
    createOptionValueChangeHandler,
    createMetafieldChangeHandler
  };
};
